import { endpoints } from '@/services/endpoints';
import {
  axiosGetRequest,
  axiosPatchRequest,
  axiosDeleteRequest,
  axiosPostRequest,
} from '@/services/Request';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Bell,
  CheckCheck,
  Trash2,
  BookOpen,
  TicketIcon,
  UserIcon,
  Send,
  X,
  BellRing,
  Users,
} from 'lucide-react';
import moment from 'moment';
import { Skeleton } from '@/components/ui/Skeleton';
import AppButton from '@/components/ui/AppButton';
import AppInput from '@/components/ui/AppInput';
import { toast } from 'react-toastify';
import { useState } from 'react';
import { useNavigate } from 'react-router';

const ROLES = ['user', 'editor', 'admin', 'superAdmin'] as const;

export default function Notifications() {
  const queryClient = useQueryClient();
  const [showSendModal, setShowSendModal] = useState(false);

  const { data: notifications, isLoading } = useQuery({
    queryKey: [endpoints.getAdminNotifications],
    queryFn: () => axiosGetRequest(endpoints.getAdminNotifications),
    select: data => data.result,
  });

  const markAsRead = useMutation({
    mutationFn: (id: string) => axiosPatchRequest(endpoints.markAsRead, {}, { id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [endpoints.getAdminNotifications] });
      queryClient.invalidateQueries({ queryKey: [endpoints.getUnreadCount] });
    },
  });

  const markAllAsRead = useMutation({
    mutationFn: () => axiosPatchRequest(endpoints.markAllAsRead),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [endpoints.getAdminNotifications] });
      queryClient.invalidateQueries({ queryKey: [endpoints.getUnreadCount] });
      toast.success('All notifications marked as read');
    },
  });

  const deleteNotification = useMutation({
    mutationFn: (id: string) => axiosDeleteRequest(endpoints.deleteNotification, { id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [endpoints.getAdminNotifications] });
      queryClient.invalidateQueries({ queryKey: [endpoints.getUnreadCount] });
      toast.success('Notification deleted');
    },
  });

  const deleteAllRead = useMutation({
    mutationFn: () => axiosDeleteRequest(endpoints.deleteAllReadNotifications),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [endpoints.getAdminNotifications] });
      toast.success('All read notifications deleted');
    },
    onError: () => toast.error('Failed to delete read notifications'),
  });

  const getIcon = (n: any) => {
    const type = n?.type || n?.title?.toLowerCase() || '';
    if (type.includes('ticket') || type.includes('report'))
      return <TicketIcon className="h-5 w-5 text-orange-500" />;
    if (type.includes('user')) return <UserIcon className="h-5 w-5 text-blue-500" />;
    return <BookOpen className="h-5 w-5 text-primary" />;
  };

  const unreadCount = notifications?.filter((n: any) => !n.isRead)?.length ?? 0;
  const readCount = notifications?.filter((n: any) => n.isRead)?.length ?? 0;
  const router = useNavigate();

  const MONGO_ID_RE = /\b([0-9a-f]{24})\b/i;

  function resolveNotificationRoute(n: any): string {
    const type = (n?.type || n?.entityType || n?.category || '').toLowerCase();
    let id = '';
    const PRIORITY = [
      'bookId',
      'book_id',
      'ticketId',
      'ticket_id',
      'entityId',
      'relatedId',
      'ref_id',
    ];
    for (const key of PRIORITY) {
      const val = n?.[key];
      if (typeof val === 'string' && MONGO_ID_RE.test(val)) {
        id = val.match(MONGO_ID_RE)![1];
        break;
      }
    }
    if (!id) {
      for (const val of Object.values(n ?? {})) {
        if (typeof val === 'string' && val !== n._id && MONGO_ID_RE.test(val)) {
          id = val.match(MONGO_ID_RE)![1];
          break;
        }
      }
    }
    if (type.includes('book')) return id ? `/books/${id}` : '/books';
    if (type.includes('ticket') || type.includes('report'))
      return id ? `/tickets/${id}` : '/tickets';
    if (type.includes('user')) return id ? `/users/profile/?uid=${id}` : '/users';
    const text = `${n?.title ?? ''} ${n?.body ?? ''}`.toLowerCase();
    if (text.includes('book')) return id ? `/books/${id}` : '/books';
    if (text.includes('ticket') || text.includes('report'))
      return id ? `/tickets/${id}` : '/tickets';
    if (text.includes('user')) return id ? `/users/profile/?uid=${id}` : '/users';
    return '/notifications';
  }

  function handleNotificationClick(n: any) {
    if (!n.isRead) markAsRead.mutate(n._id);
    router(resolveNotificationRoute(n));
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-coal-900">Notifications</h1>
          <p className="text-sm text-coal-400">
            {unreadCount > 0
              ? `You have ${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}`
              : 'All caught up!'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {readCount > 0 && (
            <AppButton
              theme="default"
              size="sm"
              onClick={() => deleteAllRead.mutate()}
              loading={deleteAllRead.isPending}
            >
              <Trash2 className="h-4 w-4" />
              Delete Read
            </AppButton>
          )}
          {unreadCount > 0 && (
            <AppButton
              theme="default"
              size="sm"
              onClick={() => markAllAsRead.mutate()}
              loading={markAllAsRead.isPending}
            >
              <CheckCheck className="h-4 w-4" />
              Mark all read
            </AppButton>
          )}
          <AppButton size="sm" onClick={() => setShowSendModal(true)}>
            <Send className="h-4 w-4" />
            Send Notification
          </AppButton>
        </div>
      </div>

      {/* List */}
      <div className="rounded-xl border border-coal-200 bg-white shadow-card">
        {isLoading ? (
          <div className="divide-y divide-coal-100">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 p-4">
                <Skeleton className="h-10 w-10 rounded-lg" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-2/3 rounded" />
                  <Skeleton className="h-3 w-1/2 rounded" />
                </div>
                <Skeleton className="h-3 w-16 rounded" />
              </div>
            ))}
          </div>
        ) : notifications?.length === 0 ? (
          <div className="flex flex-col items-center py-16">
            <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-coal-100">
              <Bell className="h-7 w-7 text-coal-400" />
            </div>
            <h3 className="text-base font-medium text-coal-700">No notifications</h3>
            <p className="mt-1 text-sm text-coal-400">You're all caught up!</p>
          </div>
        ) : (
          <div className="divide-y divide-coal-100">
            {notifications?.map((notification: any) => (
              <div
                key={notification._id}
                onClick={() => handleNotificationClick(notification)}
                className={`hover:bg-coal-50 group flex cursor-pointer items-start gap-4 p-4 transition-colors ${
                  !notification.isRead ? 'bg-red-50/40' : ''
                }`}
              >
                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-coal-100">
                  {getIcon(notification)}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h4
                        className={`text-sm capitalize leading-tight ${
                          !notification.isRead
                            ? 'font-semibold text-coal-900'
                            : 'font-medium text-coal-700'
                        }`}
                      >
                        {notification.title}
                      </h4>
                      <p className="mt-0.5 line-clamp-2 text-xs text-coal-400">
                        {notification.body}
                      </p>
                    </div>
                    <div className="flex flex-shrink-0 items-center gap-1.5">
                      {!notification.isRead && <span className="h-2 w-2 rounded-full bg-primary" />}
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-3">
                    <span className="text-[11px] text-coal-400">
                      {moment(notification.createdAt).fromNow()}
                    </span>
                    <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                      {!notification.isRead && (
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            markAsRead.mutate(notification._id);
                          }}
                          className="rounded px-1.5 py-0.5 text-[11px] text-primary transition-colors hover:bg-primary-50"
                        >
                          Mark read
                        </button>
                      )}
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          deleteNotification.mutate(notification._id);
                        }}
                        className="rounded p-1 text-coal-400 transition-colors hover:bg-danger-50 hover:text-danger"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Send Notification Modal */}
      {showSendModal && <SendNotificationModal onClose={() => setShowSendModal(false)} />}
    </div>
  );
}

// ─── Send Notification Modal (exported for reuse in UserProfile & Users table) ─

export interface SendNotificationModalProps {
  onClose: () => void;
  /** Pre-fill: send a targeted push to a specific user via testSendPush */
  targetUserId?: string;
  targetUserName?: string;
}

export function SendNotificationModal({
  onClose,
  targetUserId,
  targetUserName,
}: SendNotificationModalProps) {
  const isUserMode = Boolean(targetUserId);

  // "Broadcast" mode → adminAssignNotification (by roles)
  const [broadcastForm, setBroadcastForm] = useState({ title: '', body: '' });
  const [selectedRoles, setSelectedRoles] = useState<string[]>(['user']);

  // "User push" mode → testSendPush (by userId)
  const [pushForm, setPushForm] = useState({ title: '', body: '' });

  const broadcastMutation = useMutation({
    mutationFn: (data: any) => axiosPostRequest(endpoints.adminAssignNotification, data),
    onSuccess: () => {
      toast.success('Notification broadcast sent!');
      onClose();
    },
    onError: (e: any) => toast.error(e?.message || 'Failed to send notification'),
  });

  const pushMutation = useMutation({
    mutationFn: (data: any) => axiosPostRequest(endpoints.adminTargetedNotification, data),
    onSuccess: () => {
      toast.success(`Push notification sent to ${targetUserName || 'user'}!`);
      onClose();
    },
    onError: (e: any) => toast.error(e?.message || 'Failed to send push notification'),
  });

  function toggleRole(role: string) {
    setSelectedRoles(prev =>
      prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
    );
  }

  function handleBroadcastSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!broadcastForm.title.trim() || !broadcastForm.body.trim()) {
      toast.error('Title and body are required');
      return;
    }
    if (selectedRoles.length === 0) {
      toast.error('Select at least one role to target');
      return;
    }
    broadcastMutation.mutate({ ...broadcastForm, roles: selectedRoles });
  }

  function handlePushSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!pushForm.title.trim() || !pushForm.body.trim()) {
      toast.error('Title and body are required');
      return;
    }
    pushMutation.mutate({
      segmentUserIds: [targetUserId],
      title: pushForm.title,
      body: pushForm.body,
      gender: 'all',
      targetAudience: 'segment',
    });
  }

  return (
    <>
      <div className="fixed inset-0 z-50 bg-coal-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md rounded-xl border border-coal-200 bg-white shadow-dropdown">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-coal-200 px-6 py-4">
            <div className="flex items-center gap-2">
              {isUserMode ? (
                <BellRing className="h-5 w-5 text-primary" />
              ) : (
                <Send className="h-5 w-5 text-primary" />
              )}
              <div>
                <h3 className="text-base font-semibold text-coal-900">
                  {isUserMode ? 'Send Push Notification' : 'Send Notification'}
                </h3>
                {isUserMode && targetUserName && (
                  <p className="mt-0.5 text-xs text-coal-400">
                    To: <span className="font-medium text-coal-700">{targetUserName}</span>
                  </p>
                )}
              </div>
            </div>
            <button
              className="rounded-md p-1.5 text-coal-400 hover:bg-coal-100 hover:text-coal-600"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {isUserMode ? (
            /* ── User-specific push (testSendPush) ──────────────────── */
            <form onSubmit={handlePushSubmit} className="flex flex-col gap-4 p-6">
              <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5">
                <BellRing className="h-4 w-4 flex-shrink-0 text-blue-500" />
                <p className="text-xs text-blue-700">
                  This will send a direct FCM push notification to all devices of{' '}
                  <span className="font-semibold">{targetUserName || 'this user'}</span>.
                </p>
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-coal-700">Title *</label>
                <AppInput
                  placeholder="Notification title"
                  value={pushForm.title}
                  onChange={e => setPushForm(p => ({ ...p, title: e.target.value }))}
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-coal-700">Body *</label>
                <textarea
                  rows={3}
                  placeholder="Notification message..."
                  value={pushForm.body}
                  onChange={e => setPushForm(p => ({ ...p, body: e.target.value }))}
                  className="w-full resize-none rounded-lg border border-coal-300 p-3 text-sm text-coal-700 placeholder:text-coal-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15"
                />
              </div>

              <div className="flex gap-3 pt-1">
                <AppButton type="submit" loading={pushMutation.isPending} className="flex-1">
                  <BellRing className="h-4 w-4" />
                  Send Push
                </AppButton>
                <AppButton type="button" theme="default" onClick={onClose} className="flex-1">
                  Cancel
                </AppButton>
              </div>
            </form>
          ) : (
            /* ── Broadcast to roles (adminAssignNotification) ────────── */
            <form onSubmit={handleBroadcastSubmit} className="flex flex-col gap-4 p-6">
              <div className="bg-coal-50 flex items-center gap-2 rounded-lg border border-coal-200 px-3 py-2.5">
                <Users className="h-4 w-4 flex-shrink-0 text-coal-500" />
                <p className="text-xs text-coal-600">
                  Broadcast an in-app notification to selected user roles.
                </p>
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-coal-700">Title *</label>
                <AppInput
                  placeholder="Notification title"
                  value={broadcastForm.title}
                  onChange={e => setBroadcastForm(p => ({ ...p, title: e.target.value }))}
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-coal-700">Body *</label>
                <textarea
                  rows={3}
                  placeholder="Notification message..."
                  value={broadcastForm.body}
                  onChange={e => setBroadcastForm(p => ({ ...p, body: e.target.value }))}
                  className="w-full resize-none rounded-lg border border-coal-300 p-3 text-sm text-coal-700 placeholder:text-coal-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15"
                />
              </div>

              <div>
                <p className="mb-2 text-sm font-medium text-coal-700">Target Roles *</p>
                <div className="flex flex-wrap gap-2">
                  {ROLES.map(role => (
                    <button
                      key={role}
                      type="button"
                      onClick={() => toggleRole(role)}
                      className={`rounded-full border px-3 py-1 text-xs font-medium transition-all ${
                        selectedRoles.includes(role)
                          ? 'border-primary bg-primary text-white'
                          : 'border-coal-300 bg-white text-coal-600 hover:border-primary hover:text-primary'
                      }`}
                    >
                      {role.charAt(0).toUpperCase() + role.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-1">
                <AppButton type="submit" loading={broadcastMutation.isPending} className="flex-1">
                  <Send className="h-4 w-4" />
                  Broadcast
                </AppButton>
                <AppButton type="button" theme="default" onClick={onClose} className="flex-1">
                  Cancel
                </AppButton>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
}
